import { html, nothing, TemplateResult, unsafeCSS } from 'lit';
import { property, state } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import CryptoJS from 'crypto-js';
import ResizeObserver from 'resize-observer-polyfill';

import { ProductListRequestApiService } from '@trp-dms-digital-shared/ds-ui-library-api';

import { PopApiBaseComponent } from '../../base/pop-api-base-component';
import {
  View,
  SortField,
  SortDirection,
  SortAction,
  type ProductCenterExhibit,
  Product,
  type SelectedTableTab,
  type SelectedTileTab,
  DisclaimerPositioning,
  Disclaimer,
  type IPCT,
} from './product-center-table-wrapper.model';
import { handleMobileViewSize } from '../views/common/helper/table-helper';
import { Constants } from './product-center-table-wrapper.constants';
import { IconSVG } from '../../common/utils/icon';
import { sanitizeHTML } from '../../common/utils';
import ExcelService from '../service/excel-export/excel-service';
import Locale from '../../common/utils/locale';

import productCenterStore, {
  zustandStateToComponent,
} from '../store/product-center.store';
import { getSelectedProductsField } from '../store/product-center.store.utils';

import {
  type IFilters,
  type IDownloadProductsData,
  type IProductCenterStore,
  type ISelectedProductsTableView,
  type ISelectedProductsTileView,
} from '../store/product-center.store.model';
import { ProductCenterFragments } from '../../common/fragments';

import style from './product-center-table-wrapper.scss?inline';
import { updateUrl } from './product-center-table-wrapper.utils';

/**
 * @tag pop-pc-table-wrapper-component
 * @summary Represents a component wrapper to show Performance tables in tabs
 * @property {ProductCenterExhibit} [componentObject] ProductCenterExhibit object
 * @attribute {ProductCenterExhibit} [data-component-object] ProductCenterExhibit object
 */
export class PopProductCenterTableWrapperComponent extends PopApiBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  fragment = ProductCenterFragments.productCenterFragment;

  /**
   * @internal
   */
  @state()
  productCenterState: IProductCenterStore = productCenterStore.getState();

  /**
   * @internal
   */
  @property()
  private originalProductCenterObject: ProductCenterExhibit = {
    products: [],
    disclaimers: [],
  };

  @property()
  private filteredProducts: Product[] = [];

  @property()
  private readonly filteredProductsShown = 0;

  /**
   * @internal
   */
  @property()
  private readonly ipct: IPCT = {};

  /**
   * @internal
   */
  @property()
  private selectedView: View = View.TABLE;

  private readonly priceDisclaimers: Disclaimer[] = [];

  /**
   * @internal
   */
  @property()
  selectedTabInTableView: SelectedTableTab =
    Constants.TABLE_TAB_INDEX.PERFORMANCE;

  /**
   * @internal
   */
  @property()
  selectedTabInTileView: SelectedTileTab =
    Constants.TILES_TAB_INDEX.SINCE_INCEPTION;

  /**
   * @internal
   */
  @property()
  sortField: SortField = SortField.FUND_NAME;

  /**
   * @internal
   */
  @property()
  sortDirection: SortDirection = SortDirection.ASC;

  /**
   * @internal
   */
  @property()
  downloadProductsData!: IDownloadProductsData;

  /**
   * @internal
   */
  @property()
  selectedProductsTableView!: ISelectedProductsTableView;

  /**
   * @internal
   */
  @property()
  selectedProductsTileView!: ISelectedProductsTileView;

  /**
   * @internal
   */
  private readonly footerDisclaimers: Disclaimer[] = [];

  /**
   * @internal
   */
  @state()
  isSortPopoverOpen = false;

  /**
   * @internal
   */
  @state()
  isNoSelectedProductsErrorVisible = false;

  /**
   * @internal
   */
  private clickEventHandled = false;

  /**
   * @internal
   */
  private resizeObserver!: ResizeObserver;

  @state()
  private isSmallBreakpoint = false;

  /**
   * @internal
   */
  private localeInstance?: Locale;

  constructor() {
    super(new ProductListRequestApiService());
    this.handleResize = this.handleResize.bind(this);
    this.enableI18n = true;
  }

  /**
   * @internal
   */
  @state()
  productCenterObject?: ProductCenterExhibit;

  /**
   * @internal
   */
  private filters: IFilters = {};

  firstUpdated() {
    document.addEventListener(Constants.EVENTS.CLICK, this.handleClickOutside);
    document.addEventListener(Constants.EVENTS.FILTER_UPDATE, (e) =>
      this.filterUpdateHandler(e),
    );
    this.isSmallBreakpoint = handleMobileViewSize();

    //check if URL has params and set view to selected param
    if (window.location.search.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const selectedView = urlParams.get(Constants.UTILS.URL_PARAMS.VIEW);
      if (selectedView && this.isView(selectedView)) {
        this.productCenterState.setSelectedView(selectedView);
        this.productCenterState.resetFilters();
      }
    }
  }

  filterUpdateHandler(event: CustomEvent) {
    const { detail } = event;
    this.filters = detail.filters as IFilters;
  }

  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        this.handleSmallBreakpoint();
      });
    });
    this.resizeObserver.observe(document.body);
  }

  update(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('componentObject')) {
      if (
        this.shouldUpdatePersistantData(
          this.componentObject as ProductCenterExhibit,
          this.originalProductCenterObject,
        )
      ) {
        if (this.componentObject && this.componentObject.products) {
          productCenterStore.setState(productCenterStore.getInitialState());
          this.productCenterState.setOriginalProductCenterObject(
            this.componentObject as ProductCenterExhibit,
            this.localize(Constants.TRANSLATION_KEYS.CLASS),
          );
          this.productCenterObject = this
            .componentObject as ProductCenterExhibit;
        }
      } else {
        this.productCenterObject = this.originalProductCenterObject;
      }
    }

    this.localeInstance = new Locale(this.language ?? '', this.country ?? '');

    super.update(changedProperties);
  }

  private readonly handleClickOutside = (event: MouseEvent) => {
    if (this.clickEventHandled) {
      return;
    }
    this.clickEventHandled = true;
    const errorElement = this.shadowRoot?.querySelector(
      Constants.nestedComponents.NOTIFICATION_ERROR,
    );
    if (errorElement && !errorElement.contains(event.target as Node)) {
      this.isNoSelectedProductsErrorVisible = false;
      this.requestUpdate();
    }
    setTimeout(() => {
      this.clickEventHandled = false;
    }, 0);
  };

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    document.removeEventListener(
      Constants.EVENTS.CLICK,
      this.handleClickOutside,
    );
    super.disconnectedCallback();
  }

  protected processResponse(_response): void {
    if (_response.productCenterExhibit.products.length === 0) {
      console.warn('Products not found');
      productCenterStore.setState(productCenterStore.getInitialState());
      this.productCenterObject = {
        products: [],
        disclaimers: [],
      };
      return;
    }

    const { productCenterExhibit } = _response;
    if (
      this.shouldUpdatePersistantData(
        productCenterExhibit,
        this.originalProductCenterObject,
      )
    ) {
      if (productCenterExhibit?.products?.length > 0) {
        productCenterStore.setState(productCenterStore.getInitialState());
        this.productCenterState.setOriginalProductCenterObject(
          productCenterExhibit,
          this.localize(Constants.TRANSLATION_KEYS.CLASS),
        );
        this.productCenterObject = this.originalProductCenterObject;
      }
    } else {
      this.productCenterObject = this.originalProductCenterObject;
    }
  }

  handleResize() {
    this.isSmallBreakpoint = handleMobileViewSize();
  }

  handleSmallBreakpoint() {
    this.isSmallBreakpoint = handleMobileViewSize();
  }

  /**
   * Determines whether the persistent data should be updated based on a comparison between the component object and the state object.
   * @param componentObject - The component object to compare.
   * @param stateObject - The state object to compare.
   * @returns A boolean indicating whether the persistent data should be updated.
   */
  private shouldUpdatePersistantData(
    componentObject: ProductCenterExhibit,
    stateObject: ProductCenterExhibit,
  ): boolean {
    const hashComponentObject = this.generateHash(componentObject.products);
    const hashStateObject = this.generateHash(stateObject.products);

    if (hashComponentObject !== hashStateObject) {
      return true;
    }

    return false;
  }

  /**
   * Generates a hash value for the given object, ignoring the searchTerm property.
   * @param obj - The object to generate the hash for.
   * @returns The hash value as a string.
   */
  private generateHash(obj: any): string {
    const objWithoutSearchTerm = this.removeSearchTerm(obj);
    const jsonString = JSON.stringify(objWithoutSearchTerm);
    return CryptoJS.SHA256(jsonString).toString();
  }

  /**
   * Removes the searchTerm property from the productName object within each item of the given array of objects.
   * @param obj - The array of objects to process.
   * @returns The array of objects without the searchTerm property in productName.
   */
  private removeSearchTerm(obj: Product[]): Product[] {
    if (Array.isArray(obj)) {
      return obj.map((item) => {
        if (item.productName && item.productName.shareClassTerm !== undefined) {
          const { shareClassTerm, ...restProductName } = item.productName;
          return {
            ...item,
            productName: restProductName,
          };
        }
        return item;
      });
    }
    return [];
  }

  private isView(value: string): value is View {
    return (Object.values(View) as string[]).includes(value);
  }

  private isDownloadAvailable() {
    return (
      this.selectedView !== View.TABLE ||
      this.selectedTabInTableView === Constants.TABLE_TAB_INDEX.PERFORMANCE ||
      this.selectedTabInTableView === Constants.TABLE_TAB_INDEX.PRICE
    );
  }

  private viewChangeHandler(event: Event & { target?: { value?: string } }) {
    const value = event.target?.value ?? '';
    if (event && this.isView(value)) {
      this.productCenterState.setSelectedView(value as View);
      this.productCenterState.resetFilters();
      if (this.productCenterObject) {
        this.selectedView = value as View;
      }
    } else {
      console.warn(`Invalid value: ${value}`);
    }
    updateUrl(this.filters, this.selectedView);
  }

  private updateBaseMarginForActiveTab(value) {
    const wrappercomponent = document.querySelector(Constants.TAG_NAME);
    const beacontabs = wrappercomponent?.shadowRoot?.querySelector(
      Constants.DISCLAIMER.BEACON_TABS_TAG_NAME,
    );
    const base = beacontabs?.shadowRoot?.querySelector(
      Constants.DISCLAIMER.PRICE_DISCLAIMER_CONTAINER_TAG_ATTRIBUTE,
    );
    if (
      value === Constants.TABLE_TAB_INDEX.PRICE &&
      this.priceDisclaimers.length > 0 &&
      base
    ) {
      base.style.marginBottom = 0;
    } else if (base) {
      base.style.marginBottom = '';
    }
  }

  private tabChangeHandler(
    event: Event & { target?: { value?: SelectedTileTab } },
  ) {
    const value = +(event.target?.value ?? Constants.TILES_TAB_INDEX.ONE_YEAR);
    this.productCenterState.tabChangeHandler(value);

    if (this.productCenterObject) {
      if (this.isTableView()) {
        this.updateBaseMarginForActiveTab(value);
        this.selectedTabInTableView = value as SelectedTableTab;
      } else {
        this.selectedTabInTileView = value as SelectedTileTab;
      }
    }

    this.requestUpdate();
  }

  private sortClickHandler(
    sort: SortField | SortDirection,
    action: SortAction,
  ) {
    if (action === SortAction.SORT_BY) {
      this.productCenterState.setSortInTileView(sort as SortField, undefined);
    } else {
      this.productCenterState.setSortInTileView(
        undefined,
        sort as SortDirection,
      );
    }
  }

  private initializeProductCenterstore() {
    if (!productCenterStore.getState().storeInitialized) {
      this.productCenterState.setOriginalProductCenterObject(
        this.productCenterObject as ProductCenterExhibit,
        this.localize(Constants.TRANSLATION_KEYS.CLASS),
      );
    }
  }

  private async _downloadExcel(): Promise<void> {
    this.isNoSelectedProductsErrorVisible = false;
    const selectedTabInView = this.isTableView()
      ? this.selectedTabInTableView
      : this.selectedTabInTileView;
    const selectedProductsField = getSelectedProductsField(
      this.selectedView,
      selectedTabInView,
    );
    const selectedProducts = this.isTableView()
      ? this.selectedProductsTableView[selectedProductsField]
      : this.selectedProductsTileView[selectedProductsField];
    if (selectedProducts.length === 0) {
      this.isNoSelectedProductsErrorVisible = true;
      return;
    }

    const excelService = new ExcelService(this.localeInstance);
    await excelService.generateExcelFile();
  }

  private isTableView(): boolean {
    return this.selectedView === View.TABLE;
  }

  private renderSortButton(id: string) {
    return html` <beacon-button
      id="sort-button-trigger${id}"
      class="beacon-sort-button"
      icon-align="right"
      variant="tertiary"
      scale="normal"
    >
      ${IconSVG.chevronDown({
        slot: Constants.SUFFIX_SLOT,
        customClass: Constants.CHEVRON_CUSTOM_CLASS,
      })}
      ${this.localize(Constants.TRANSLATION_KEYS.SORT.SORT)}
    </beacon-button>`;
  }

  private renderSortByElements(prefix?: string) {
    let productIdentifierValue = Constants.TRANSLATION_KEYS.APIR;
    let sortField = SortField.APIR;
    this.ipct?.configuration?.productIdentifiers?.forEach((identifier) => {
      if (Constants.FUND_FIELD[identifier] !== undefined) {
        productIdentifierValue = Constants.TRANSLATION_KEYS[identifier];
        sortField = SortField[identifier];
      }
    });

    return html`
      <div class="sort-field-list">
        <beacon-control-group class="beacon-sort-control">
          ${this.renderRadioButton(
            `sort-${SortField.FUND_NAME}${prefix}`,
            this.localize(Constants.TRANSLATION_KEYS.PRODUCT_NAME),
            SortField.FUND_NAME,
            prefix,
          )}
          ${productIdentifierValue
            ? this.renderRadioButton(
                `sort-${SortField.APIR}${prefix}`,
                this.localize(productIdentifierValue),
                sortField,
                prefix,
              )
            : nothing}
        </beacon-control-group>
        <div class="horizontal-divider"></div>
        <div class="sort-container">
          ${this.renderSortDirectionButton(
            SortDirection.ASC,
            this.localize(Constants.TRANSLATION_KEYS.SORT.SORT_ASC),
            undefined,
          )}
          ${this.renderSortDirectionButton(
            SortDirection.DSC,
            this.localize(Constants.TRANSLATION_KEYS.SORT.SORT_DSC),
            Constants.SORT_DIRECTION_DSC_CUSTOM_CLASS,
          )}
        </div>
      </div>
    `;
  }

  private renderSortPopover(id: number) {
    return html`
      ${this.renderSortButton(`${id}`)}
      <beacon-popover
        id="sort-popover"
        opens-by="#sort-button-trigger${id}"
        placement="bottom-start"
      >
        ${this.renderSortByElements(`${id}`)}
      </beacon-popover>
    `;
  }

  private renderRadioButton(
    id: string,
    label: string,
    value: SortField,
    prefix?: string,
  ) {
    return html` <beacon-control inline="" scale="mini">
      <label for="${id}" slot="label">${label}</label>
      ${keyed(
        `${id}`,
        html` <input
          type="radio"
          name="sort-options${prefix}"
          id="${id}"
          .checked=${this.sortField === value}
          @click="${() => this.sortClickHandler(value, SortAction.SORT_BY)}"
        />`,
      )}
    </beacon-control>`;
  }

  private renderSortDirectionButton(
    sortDirection: SortDirection,
    label: string,
    customClass: string | undefined,
  ) {
    return html`
      <beacon-button
        class="beacon-sort-action-button"
        icon-align="left"
        variant="tertiary"
        scale="mini"
        @beacon-event-click=${() => {
          this.sortClickHandler(sortDirection, SortAction.SORT_DIRECTION);
        }}
      >
        ${IconSVG.sortArrow({
          slot: Constants.PREFIX_SLOT,
          customClass: customClass,
        })}
        <span
          class="${this.sortDirection === sortDirection
            ? 'current-sort-direction'
            : 'unselected-sort-direction'} beacon-sort-direction"
        >
          ${label}
        </span>
      </beacon-button>
    `;
  }

  private renderTilePanelView(tabIdex: number) {
    const showDownloadActions =
      this.isSmallBreakpoint && this.isDownloadAvailable();

    const downloadActionsTemplate = showDownloadActions
      ? this.renderDownloadActions()
      : nothing;

    if (this.selectedTabInTileView === tabIdex) {
      return html`
        ${downloadActionsTemplate}
        <pop-pc-performance-tiles-component
          .country="${this.country}"
          .language="${this.language}"
        ></pop-pc-performance-tiles-component>
      `;
    } else {
      return html``;
    }
  }

  private renderTabPanel(panelNumber: number, content: TemplateResult) {
    const isChartTilesView = !this.isTableView();
    const shouldRenderDownloadActions =
      this.isTableView() &&
      (this.selectedTabInTableView === Constants.TABLE_TAB_INDEX.PERFORMANCE ||
        this.selectedTabInTableView === Constants.TABLE_TAB_INDEX.PRICE);

    const actionsTemplate = isChartTilesView
      ? this.renderActions(panelNumber)
      : nothing;
    const downloadActionsTemplate = shouldRenderDownloadActions
      ? this.renderDownloadActions()
      : nothing;

    return html`
      <beacon-tab-panel
        class="pop-tab-panel"
        slot="tab-panel"
        value="${panelNumber}"
      >
        ${downloadActionsTemplate} ${actionsTemplate} ${content}
      </beacon-tab-panel>
    `;
  }

  private renderActions(panelId: number) {
    return html`
      <div class="actions-container">
        <span
          class="b-typography-sans-serif--caption--300 b-font-weight-bold action"
        >
          ${this.renderSortPopover(panelId)}
        </span>
      </div>
    `;
  }

  private renderDownloadActions() {
    return html`
      <div
        class="${
          this.isSmallBreakpoint
            ? 'download-container-panel'
            : 'download-container'
        }
          ${
            this.isTableView() && !this.isSmallBreakpoint
              ? 'download-container-table-view'
              : ''
          }
          "
      >
        <span
          @click="${() =>
            this.productCenterState.toggleCheckAllProducts(
              this.downloadProductsData.selectedProductsField,
            )}"
          class="b-typography-sans-serif--caption--300 b-font-weight-bold action"
        >
          ${
            this.downloadProductsData.isAllProductSelected
              ? this.localize(Constants.TRANSLATION_KEYS.ACTIONS.DESELECT_ALL)
              : this.localize(Constants.TRANSLATION_KEYS.ACTIONS.SELECT_ALL)
          }
        </span>
        <div class="divider"></div>
        <beacon-button
          @beacon-event-click="${() => this._downloadExcel()}"
          class="download-button"
          icon-align="left"
          variant="secondary"
          scale="normal"
        >
          ${IconSVG.download({
            slot: Constants.PREFIX_SLOT,
          })}
          <span class="download-button-label">
            ${this.localize(Constants.TRANSLATION_KEYS.ACTIONS.DOWNLOAD)}
            <span class="total-products-download"
              >(${this.downloadProductsData.totalProducts})</span
            >
          <span>
        </beacon-button>
      </div>
    `;
  }

  private renderTab(tabNumber: number, key: string, active: boolean) {
    return html`
      <beacon-tab
        slot="tab-children"
        label="${this.localize(key)}"
        value="${tabNumber}"
        ${active ? 'active' : ''}
        @beacon-event-click="${this.tabChangeHandler}"
      >
      </beacon-tab>
    `;
  }

  private renderTabs() {
    return this.isTableView() ? this.renderTableView() : this.renderTileView();
  }

  private renderTableView() {
    return html`
      <div class="tabs-container">
        <beacon-tabs
          variant="secondary"
          tab-type="standard"
          selected="${this.selectedTabInTableView}"
        >
          ${this.renderTab(
            Constants.TABLE_TAB_INDEX.PERFORMANCE,
            Constants.TRANSLATION_KEYS.TABS.PERFORMANCE,
            true,
          )}
          ${this.renderTab(
            Constants.TABLE_TAB_INDEX.PRICE,
            Constants.TRANSLATION_KEYS.TABS.PRICE,
            false,
          )}
          ${this.renderTab(
            Constants.TABLE_TAB_INDEX.ESG,
            Constants.TRANSLATION_KEYS.TABS.ESG,
            false,
          )}
          ${this.renderTab(
            Constants.TABLE_TAB_INDEX.DOCUMENTS,
            Constants.TRANSLATION_KEYS.TABS.DOCUMENTS,
            false,
          )}
          ${this.renderTabPanelsForTable()}
        </beacon-tabs>
      </div>
    `;
  }

  private renderTileView() {
    return html`
      <div class="tabs-container">
        <beacon-tabs
          variant="secondary"
          tab-type="standard"
          selected="${this.selectedTabInTileView}"
        >
          ${this.renderTab(
            Constants.TILES_TAB_INDEX.ONE_YEAR,
            Constants.TRANSLATION_KEYS.FUND_RETURN.ONE_YEAR,
            true,
          )}
          ${this.renderTab(
            Constants.TILES_TAB_INDEX.THREE_YEARS,
            Constants.TRANSLATION_KEYS.FUND_RETURN.THREE_YEARS,
            false,
          )}
          ${this.renderTab(
            Constants.TILES_TAB_INDEX.FIVE_YEARS,
            Constants.TRANSLATION_KEYS.FUND_RETURN.FIVE_YEARS,
            false,
          )}
          ${this.renderTab(
            Constants.TILES_TAB_INDEX.TEN_YEARS,
            Constants.TRANSLATION_KEYS.FUND_RETURN.TEN_YEARS,
            false,
          )}
          ${this.renderTab(
            Constants.TILES_TAB_INDEX.SINCE_INCEPTION,
            Constants.TRANSLATION_KEYS.FUND_RETURN.SINCE_INCEPTION,
            false,
          )}
          ${this.renderTabPanelsForTiles()}
        </beacon-tabs>
      </div>
    `;
  }

  private renderDownloadActionsIfNeeded() {
    return !this.isSmallBreakpoint && this.isDownloadAvailable()
      ? this.renderDownloadActions()
      : nothing;
  }

  private renderTabPanelsForTable() {
    const hasFilteredProducts =
      this.filteredProducts && this.filteredProducts.length > 0;
    return html`
      ${hasFilteredProducts
        ? this.renderTabPanel(
            Constants.TABLE_TAB_INDEX.PERFORMANCE,
            html`<pop-pc-performance-table-component
              .country="${this.country}"
              .language="${this.language}"
            ></pop-pc-performance-table-component>`,
          )
        : nothing}
      ${hasFilteredProducts
        ? this.renderTabPanel(
            Constants.TABLE_TAB_INDEX.PRICE,
            html`
              <pop-pc-price-table-component
                .country="${this.country}"
                .language="${this.language}"
              ></pop-pc-price-table-component>
            `,
          )
        : nothing}
      ${hasFilteredProducts
        ? this.renderTabPanel(
            Constants.TABLE_TAB_INDEX.ESG,
            html`<pop-pc-esg-table-component
              .country="${this.country}"
              .language="${this.language}"
            ></pop-pc-esg-table-component>`,
          )
        : nothing}
      ${hasFilteredProducts
        ? this.renderTabPanel(
            Constants.TABLE_TAB_INDEX.DOCUMENTS,
            html`<pop-pc-documents-table-component
              .country="${this.country}"
              .language="${this.language}"
            ></pop-pc-documents-table-component>`,
          )
        : nothing}
    `;
  }

  private renderTabPanelsForTiles() {
    return html`
      ${this.renderTabPanel(
        Constants.TILES_TAB_INDEX.ONE_YEAR,
        this.renderTilePanelView(Constants.TILES_TAB_INDEX.ONE_YEAR),
      )}
      ${this.renderTabPanel(
        Constants.TILES_TAB_INDEX.THREE_YEARS,
        this.renderTilePanelView(Constants.TILES_TAB_INDEX.THREE_YEARS),
      )}
      ${this.renderTabPanel(
        Constants.TILES_TAB_INDEX.FIVE_YEARS,
        this.renderTilePanelView(Constants.TILES_TAB_INDEX.FIVE_YEARS),
      )}
      ${this.renderTabPanel(
        Constants.TILES_TAB_INDEX.TEN_YEARS,
        this.renderTilePanelView(Constants.TILES_TAB_INDEX.TEN_YEARS),
      )}
      ${this.renderTabPanel(
        Constants.TILES_TAB_INDEX.SINCE_INCEPTION,
        this.renderTilePanelView(Constants.TILES_TAB_INDEX.SINCE_INCEPTION),
      )}
    `;
  }

  private renderToggleButton(view: View, label: string) {
    const isSelected = this.selectedView === view;
    const icon = view === View.TABLE ? IconSVG.tableView : IconSVG.chartView;

    return html`
      <beacon-segmented-control
        id="${view}"
        value="${view}"
        ?selected=${isSelected}
      >
        ${icon({ slot: Constants.ICON_SLOT })} ${label}
      </beacon-segmented-control>
    `;
  }

  private renderToggleView() {
    return this.ipct?.configuration?.chartView
      ? html`
          <beacon-segmented-controls
            value="${this.selectedView}"
            scale="mini"
            @beacon-event-input="${this.viewChangeHandler}"
            class="pop-toggle-view"
          >
            ${this.renderToggleButton(
              View.TABLE,
              this.localize(Constants.TRANSLATION_KEYS.VIEWS.TABLE),
            )}
            ${this.renderToggleButton(
              View.CHART_TILES,
              this.localize(Constants.TRANSLATION_KEYS.VIEWS.CHART),
            )}
          </beacon-segmented-controls>
        `
      : nothing;
  }

  private renderHeadings() {
    const filteredProductsShown = this.filteredProductsShown ?? 0;
    const totalProducts =
      this.originalProductCenterObject?.products?.length ?? 0;
    const isChartTilesView = this.selectedView === View.CHART_TILES;
    const noSelectedProductsError = this.isNoSelectedProductsErrorVisible
      ? this.renderNoSelectedProductsError()
      : nothing;
    const downloadActions = isChartTilesView
      ? this.renderDownloadActionsIfNeeded()
      : nothing;

    const sortButtonAndModal = this.isSmallBreakpoint
      ? html`
          ${this.renderSortButton(Constants.MODAL_PREFIX)}
          <beacon-modal
            opens-by="#sort-button-trigger${Constants.MODAL_PREFIX}"
          >
            <h2 class="b-typography-serif--headline--100 sort-modal-title">
              ${this.localize(Constants.TRANSLATION_KEYS.SORT_BY)}
            </h2>
            <div class="b-flow-space">
              ${this.renderSortByElements(Constants.MODAL_PREFIX)}
            </div>
          </beacon-modal>
        `
      : nothing;

    return html`
      <div class="b-flex b-justify-between pop-toggle-containter">
        <div class="b-typography-sans-serif--caption--200 pop-wrapper-head">
          ${unsafeHTML(
            this.localize(Constants.TRANSLATION_KEYS.SHOWING_PRODUCTS, {
              numberOfProductsShown: `<span class="b-typography-sans-serif--caption--200 b-font-weight-bold">${filteredProductsShown}</span>`,
              numberOfProducts: `<span class="b-typography-sans-serif--caption--200 b-font-weight-bold">${totalProducts}</span>`,
            }),
          )}
        </div>
        <div class="custom-toggle b-flex b-justify-between">
          ${this.renderToggleView()}
        </div>
      </div>
      ${!this.isTableView()
        ? html`
            ${noSelectedProductsError}
            <div
              class="b-flex b-justify-between b-align-center custom-toggle pop-cumulative-header-containter"
            >
              <div
                class="b-typography-sans-serif--body--100 b-font-weight-bold"
              >
                ${this.localize(
                  Constants.TRANSLATION_KEYS.PERFORMANCE_TILE_TITLE,
                )}
              </div>
              ${sortButtonAndModal} ${downloadActions}
            </div>
          `
        : nothing}
    `;
  }

  private renderNoSelectedProductsError() {
    return html`
      <beacon-notification variant="error" class="no-selected-products-error">
        ${Constants.DOWNLOAD_EXPORT_ERROR.TITLE}
        <span slot="description">
          ${Constants.DOWNLOAD_EXPORT_ERROR.DESCRIPTION}
        </span>
      </beacon-notification>
    `;
  }

  renderComponent() {
    this.initializeProductCenterstore();
    return html`
      <div class="pop-tables-wrapper b-padding--organism-normal">
        <div class="pop-header-container">
          <div class="pop-header-wrapper">
            <h1 class="pop-performance-heading" role="banner">
              ${this.localize(Constants.TRANSLATION_KEYS.PRODUCT_EXPLORER)}
            </h1>
          </div>
        </div>
        <pop-pc-search-bar
          .country=${this.country}
          .language=${this.language}
        ></pop-pc-search-bar>
        <pop-pc-filters-component
          .country=${this.country}
          .language=${this.language}
        ></pop-pc-filters-component>
        <pop-pc-filter-chips-component
          .country=${this.country}
          .language=${this.language}
        ></pop-pc-filter-chips-component>
        <section class="pop-performance-tables">
          ${this.renderHeadings()}
          ${this.selectedView === View.TABLE &&
          this.isNoSelectedProductsErrorVisible
            ? this.renderNoSelectedProductsError()
            : nothing}
          ${this.renderTabs()}
        </section>
        ${this.footerDisclaimers.length > 0
          ? html`
              <footer class="footer-disclaimers">
                <pop-disclosure-component>
                  ${this.footerDisclaimers.map((disclaimer: Disclaimer) =>
                    unsafeHTML(
                      sanitizeHTML(`${disclaimer.disclaimerText.html}`),
                    ),
                  )}
                </pop-disclosure-component>
              </footer>
            `
          : nothing}
      </div>
    `;
  }

  render() {
    if (!this.productCenterObject || !this.isI18nLoaded) {
      return html``;
    }

    return this.renderComponent();
  }
}

export const productCenterTableWrapperMapState = (productCenterState) => {
  return {
    ipct: productCenterState.ipct,
    originalProductCenterObject: productCenterState.originalProductCenterObject,
    filteredProducts: productCenterState.getFilteredProducts(),
    filteredProductsShown: productCenterState.getFilteredProductsShown(),
    selectedView: productCenterState.selectedView,
    selectedTabInTableView: productCenterState.selectedTabInTableView,
    selectedTabInTileView: productCenterState.performanceTileView.selectedTab,
    sortField: productCenterState.performanceTileView.sortField,
    sortDirection: productCenterState.performanceTileView.sortDirection,
    footerDisclaimers: productCenterState.getDisclaimersByPositioning(
      DisclaimerPositioning.FOOTER,
    ),
    downloadProductsData: productCenterState.getDownloadProductsData(),
    selectedProductsTableView: productCenterState.selectedProducts,
    selectedProductsTileView:
      productCenterState.performanceTileView.selectedProducts,
  };
};

if (!customElements.get(Constants.TAG_NAME)) {
  customElements.define(
    Constants.TAG_NAME,
    zustandStateToComponent(
      {
        mapState: (productCenterState) => {
          return {
            ...productCenterTableWrapperMapState(productCenterState),
            priceDisclaimers: productCenterState.getDisclaimersByPositioning(
              DisclaimerPositioning.PRICE,
            ),
          };
        },
      },
      PopProductCenterTableWrapperComponent,
    ),
  );
}
